import { SearchIcon } from "lucide-react";
import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { cn } from "@/lib/utils.ts";

export type CommandItem<T> = {
  id: string;
  value: T;
  /** A row that is shown for context but cannot be picked. */
  disabled?: boolean;
  content: ReactNode;
};

/**
 * A command-style list: a search input and a listbox navigated with the arrow keys.
 * Focus stays in the input; the highlighted option is announced with `aria-activedescendant`.
 */
export function Command<T>({
  query,
  onQueryChange,
  items,
  onSelect,
  placeholder,
  label,
  status,
  autoFocus = true,
  className,
  listClassName,
}: {
  query: string;
  onQueryChange: (query: string) => void;
  items: Array<CommandItem<T>>;
  onSelect: (value: T) => void;
  placeholder: string;
  label: string;
  /** Shown instead of the list: a hint, a spinner, or "nothing found". */
  status?: ReactNode;
  autoFocus?: boolean;
  className?: string;
  listClassName?: string;
}) {
  const listId = useId();
  const [active, setActive] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const enabled = items.filter((item) => !item.disabled);

  // Highlight the first selectable row whenever the results change.
  const firstEnabled = enabled[0]?.id ?? null;
  const signature = items.map((item) => item.id).join("|");
  useEffect(() => {
    setActive(firstEnabled);
  }, [signature, firstEnabled]);

  useEffect(() => {
    if (!active) return;
    const node = listRef.current?.querySelector<HTMLElement>(`[data-command-id="${CSS.escape(active)}"]`);
    node?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const move = (delta: number) => {
    if (enabled.length === 0) return;
    const index = enabled.findIndex((item) => item.id === active);
    const next = enabled[(index + delta + enabled.length) % enabled.length];
    if (next) setActive(next.id);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      move(1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      move(-1);
    } else if (event.key === "Home" && enabled[0]) {
      event.preventDefault();
      setActive(enabled[0].id);
    } else if (event.key === "End" && enabled.length > 0) {
      event.preventDefault();
      setActive(enabled[enabled.length - 1]?.id ?? null);
    } else if (event.key === "Enter") {
      const item = enabled.find((candidate) => candidate.id === active);
      if (item) {
        event.preventDefault();
        onSelect(item.value);
      }
    }
  };

  const activeDescendant = active ? `${listId}-${active}` : undefined;

  return (
    <div className={cn("flex min-w-0 flex-col", className)}>
      <div className="flex items-center gap-2 border-b px-3">
        <SearchIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        <input
          role="combobox"
          aria-expanded
          aria-controls={listId}
          aria-activedescendant={activeDescendant}
          aria-autocomplete="list"
          aria-label={label}
          autoFocus={autoFocus}
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          className="h-10 min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground/80"
          spellCheck={false}
          autoComplete="off"
        />
      </div>
      <div ref={listRef} id={listId} role="listbox" aria-label={label} className={cn("max-h-72 overflow-y-auto p-1", listClassName)}>
        {status ? <div className="px-3 py-6 text-center text-[13px] text-muted-foreground">{status}</div> : null}
        {!status
          ? items.map((item) => (
              <div
                key={item.id}
                id={`${listId}-${item.id}`}
                data-command-id={item.id}
                role="option"
                aria-selected={item.id === active}
                aria-disabled={item.disabled || undefined}
                onMouseMove={() => {
                  if (!item.disabled && active !== item.id) setActive(item.id);
                }}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  if (!item.disabled) onSelect(item.value);
                }}
                className={cn(
                  "rounded-md px-2 py-1.5 text-[13px]",
                  item.disabled ? "cursor-default text-muted-foreground" : "cursor-pointer",
                  item.id === active && !item.disabled && "bg-accent text-accent-foreground",
                )}
              >
                {item.content}
              </div>
            ))
          : null}
      </div>
    </div>
  );
}
